// Backoffice operators + permission groups + the logged-in operator's profile.
// These are Yep "user"/"group" entities — NOT regular content models — so they live on their
// own REST endpoints (mirrors booking/website/public/admin/yep-admin/core/api/Api.js):
//   GET|POST|DELETE  /admin/rest/user[/{id}]
//   GET|POST|DELETE  /admin/rest/group[/{id}]
//   POST             /admin/rest/profile          (save my own profile)
// Field shapes come from the legacy DB (t_yep_user / t_yep_user_group f_content JSON).
import { ApiError, notifyUnauthorized } from './client'

const BASE = '/admin'

export interface Perm {
  read: boolean
  write: boolean
  edit: boolean
  delete: boolean
}
export interface Grants {
  user?: Perm
  page?: Record<string, unknown>
  block?: Record<string, unknown>
  media?: Record<string, unknown>
  content?: Record<string, Perm>
  [k: string]: unknown
}
export interface YepUser {
  id?: string
  modelId?: string
  username: string
  password?: string
  name: string
  email: string
  language?: string
  enabled: boolean
  creationDate?: string
  lastUpdate?: string
}
export interface YepGroup {
  id?: string
  modelId?: string
  name: string
  userIds?: string[]
  languages?: string[]
  grants?: Grants
  creationDate?: string
  lastUpdate?: string
}

async function reqJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  })
  if (res.status === 401) notifyUnauthorized()
  if (!res.ok) {
    const raw = await res.text().catch(() => '')
    let msg = `HTTP ${res.status}`
    try {
      const j = JSON.parse(raw) as { message?: string }
      if (j?.message) msg = j.message
    } catch {
      if (raw) msg = `HTTP ${res.status}: ${raw.slice(0, 200)}`
    }
    throw new ApiError(msg, res.status)
  }
  const text = await res.text()
  try {
    return JSON.parse(text) as T
  } catch {
    return undefined as unknown as T
  }
}

// Yep list endpoints may return a bare array OR { data, totalCount }; normalize both.
function asList<T>(v: unknown): T[] {
  if (Array.isArray(v)) return v as T[]
  if (v && typeof v === 'object' && Array.isArray((v as { data?: unknown }).data)) return (v as { data: T[] }).data
  return []
}

/* ── operators (yep_user) ─────────────────────────────────────────── */
export async function listUsers(): Promise<YepUser[]> {
  return asList<YepUser>(await reqJson<unknown>(`${BASE}/rest/user?language=it&offset=0&limit=500`))
}
export function getUser(id: string): Promise<YepUser> {
  return reqJson<YepUser>(`${BASE}/rest/user/${encodeURIComponent(id)}?language=it`)
}
export function saveUser(user: YepUser): Promise<YepUser> {
  const url = user.id ? `${BASE}/rest/user/${encodeURIComponent(user.id)}` : `${BASE}/rest/user`
  return reqJson<YepUser>(url, { method: 'POST', body: JSON.stringify({ language: 'it', ...user }) })
}
export function deleteUser(id: string): Promise<void> {
  return reqJson<void>(`${BASE}/rest/user/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/* ── permission groups (yep_user_group) ───────────────────────────── */
export async function listGroups(): Promise<YepGroup[]> {
  return asList<YepGroup>(await reqJson<unknown>(`${BASE}/rest/group?language=it&offset=0&limit=500`))
}
export function getGroup(id: string): Promise<YepGroup> {
  return reqJson<YepGroup>(`${BASE}/rest/group/${encodeURIComponent(id)}?language=it`)
}
export function saveGroup(group: YepGroup): Promise<YepGroup> {
  const url = group.id ? `${BASE}/rest/group/${encodeURIComponent(group.id)}` : `${BASE}/rest/group`
  return reqJson<YepGroup>(url, { method: 'POST', body: JSON.stringify({ language: 'it', ...group }) })
}
export function deleteGroup(id: string): Promise<void> {
  return reqJson<void>(`${BASE}/rest/group/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/* ── my own profile ───────────────────────────────────────────────── */
export function saveProfile(data: { name?: string; email?: string; language?: string; password?: string }): Promise<unknown> {
  return reqJson<unknown>(`${BASE}/rest/profile`, { method: 'POST', body: JSON.stringify(data) })
}
